import * as React from 'react';

import { Svg } from 'src/styledComponents/Icons';
import { Utility } from 'src/styledComponents/Typography';

import { PaginationContainer, PaginationElement, ArrowLeft, ArrowRight, Description } from './Components';

const DOTS = 'DOTS';

export enum DescriptionVariants {
  articles = 'articles',
  base = 'base',
}

type Props = {
  className?: string;
  onPageChanged: (page: number) => void;
  totalRecords?: number;
  pageLimit?: number;
  pageNeighbours?: 1 | 2;
  currentPage: number;
  descriptionVariant?: DescriptionVariants;
};

export const ControlledPagination: React.FC<Props> = (props) => {
  const {
    className,
    onPageChanged,
    totalRecords = 0,
    pageLimit = 15,
    pageNeighbours = 1,
    currentPage,
    descriptionVariant = DescriptionVariants.articles,
  } = props;

  const totalPages: number = Math.ceil(totalRecords / pageLimit);

  const range = (from, to, step = 1) => {
    let i = from;
    const range: any[] = [];

    while (i <= to) {
      range.push(i);
      i += step;
    }

    return range;
  };

  const fetchPageNumbers = () => {
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (totalPages > totalBlocks) {
      let pages: any = [];

      const leftBound = currentPage - pageNeighbours;
      const rightBound = currentPage + pageNeighbours;
      const beforeLastPage = totalPages - 1;

      const startPage = leftBound > 2 ? leftBound : 2;
      const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;

      pages = range(startPage, endPage);

      const pagesCount = pages.length;
      const singleSpillOffset = totalNumbers - pagesCount - 1;

      const leftSpill = startPage > 2;
      const rightSpill = endPage < beforeLastPage;

      if (leftSpill && !rightSpill) {
        const extraPages = range(startPage - singleSpillOffset, startPage - 1);
        pages = [DOTS, ...extraPages, ...pages];
      } else if (!leftSpill && rightSpill) {
        const extraPages = range(endPage + 1, endPage + singleSpillOffset);
        pages = [...pages, ...extraPages, DOTS];
      } else if (leftSpill && rightSpill) {
        pages = [DOTS, ...pages, DOTS];
      }

      return [1, ...pages, totalPages];
    }

    return range(1, totalPages);
  };

  const getDescription = () => {
    const from = currentPage > 1 ? (currentPage - 1) * pageLimit : currentPage;
    const to = Math.min(currentPage * pageLimit, totalRecords);

    return `${from} - ${to} of ${totalRecords}${descriptionVariant === DescriptionVariants.articles ? ' Articles' : ''}`;
  };

  if (!totalRecords || totalPages === 1) return null;

  const pages = fetchPageNumbers();

  const handleClick = (page, evt) => {
    if (page === DOTS) {
      return;
    }

    evt.preventDefault();
    onPageChanged(page);
  };

  const handleMoveLeft = (evt) => {
    evt.preventDefault();
    if (currentPage === 1) {
      return;
    }
    onPageChanged(currentPage - 1);
  };

  const handleMoveRight = (evt) => {
    evt.preventDefault();
    if (currentPage === totalPages) {
      return;
    }
    onPageChanged(currentPage + 1);
  };

  return (
    <div className={className}>
      <PaginationContainer>
        <ArrowLeft key='btn-left' className='page-item' onClick={handleMoveLeft} disabled={currentPage === 1}>
          <Svg name='ArrowDown' size={16} {...testAnchors.useButton('previous-page')} />
        </ArrowLeft>

        {pages.map((page, index) => (
          <PaginationElement
            active={currentPage === page}
            key={index}
            onClick={(e) => handleClick(page, e)}
            {...testAnchors.useButton(`page.${page}`)}
          >
            {page === DOTS ? (
              <Svg name='ThreeDots' size={10} />
            ) : (
              <Utility variant='u4' weight='medium'>
                {page}
              </Utility>
            )}
          </PaginationElement>
        ))}

        <ArrowRight key='btn-right' className='page-item' onClick={handleMoveRight} disabled={currentPage === totalPages}>
          <Svg name='ArrowDown' size={16} {...testAnchors.useButton('next-page')} />
        </ArrowRight>
      </PaginationContainer>

      <Description variant='body2'>{getDescription()}</Description>
    </div>
  );
};
